/**
 * Editable-workspace-processes hooks (migration 0154, PR3) — TanStack Query
 * over workspaceProcessesApi.
 *
 * Three reads (the process list, a process's edit-mode stages incl. inactive,
 * a template's items) + one generic COMMIT mutation. The mutating endpoints all
 * share the dry-run -> impact-facts protocol; the editor components call the api
 * with dryRun=true directly for the warning numbers (they need them
 * synchronously before showing the confirm), then run the same call as a real
 * commit through useProcessCommand so the affected caches invalidate. One
 * implementation, no parallel mutation-per-action sprawl.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workspaceProcessesApi,
  type WorkProcessWire,
  type WorkProcessStageWire,
  type WorkTemplateItemWire,
} from '@/lib/api';

export type { WorkProcessWire, WorkProcessStageWire, WorkTemplateItemWire };

const STALE_MS = 30_000;

export function workspaceProcessesKey(slug: string): readonly unknown[] {
  return ['workspace-processes', slug] as const;
}
export function processStagesKey(slug: string, processId: string): readonly unknown[] {
  return ['workspace-process-stages', slug, processId] as const;
}
export function templateItemsKey(slug: string, templateId: string): readonly unknown[] {
  return ['workspace-template-items', slug, templateId] as const;
}

/** The visible process boards (server visibility-filters the list). */
export function useWorkspaceProcesses(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ processes: WorkProcessWire[]; request_id: string }>({
    queryKey: workspaceProcessesKey(slug),
    queryFn: ({ signal }) => workspaceProcessesApi.list(slug, signal),
    staleTime: STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    processes: query.data?.processes ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Edit-mode stage list for a process — ACTIVE first, then inactive. */
export function useProcessStages(slug: string, processId: string | null, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ stages: WorkProcessStageWire[]; request_id: string }>({
    queryKey: processStagesKey(slug, processId ?? ''),
    queryFn: ({ signal }) => workspaceProcessesApi.stages(slug, processId!, signal),
    staleTime: STALE_MS,
    enabled: (opts.enabled ?? true) && !!processId,
  });
  return { stages: query.data?.stages ?? [], isLoading: query.isLoading, isError: query.isError };
}

/** The checklist-template items for a process's template (the editor). */
export function useTemplateItems(slug: string, templateId: string | null, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ items: WorkTemplateItemWire[]; request_id: string }>({
    queryKey: templateItemsKey(slug, templateId ?? ''),
    queryFn: ({ signal }) => workspaceProcessesApi.templateItems(slug, templateId!, signal),
    staleTime: STALE_MS,
    enabled: (opts.enabled ?? true) && !!templateId,
  });
  return { items: query.data?.items ?? [], isLoading: query.isLoading, isError: query.isError };
}

/**
 * A generic COMMIT mutation: the caller passes a thunk that runs the real
 * (non-dry-run) api call; on success EVERY affected cache is invalidated (the
 * process list, all process-stage + template-item reads, and the board's
 * work-items + stages — a stage/label/tier edit can move cards + counts). One
 * mutation instance per editor mount; edit actions are one-at-a-time.
 */
export function useProcessCommand<T>(slug: string) {
  const qc = useQueryClient();
  return useMutation<T, unknown, { run: () => Promise<T> }>({
    mutationFn: (vars) => vars.run(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-processes', slug] });
      void qc.invalidateQueries({ queryKey: ['workspace-process-stages', slug] });
      void qc.invalidateQueries({ queryKey: ['workspace-template-items', slug] });
      void qc.invalidateQueries({ queryKey: ['workspace-stages', slug] });
      void qc.invalidateQueries({ queryKey: ['work-items', slug] });
      void qc.invalidateQueries({ queryKey: ['work-notifications', slug] });
    },
  });
}
